<script lang="ts" setup>
import { ref, computed } from 'vue'
import RoomieChipSelector from '@lahaus-roomie/roomie/src/components/RoomieChipSelector/index.vue'
import RoomieIcon from '@lahaus-roomie/roomie/src/components/RoomieIcon/index.vue'
import CloseIcon from '@lahaus-roomie/roomie/src/assets/icons/close.svg'

import type { Props } from './types'
import { useAppChatStore } from '@/stores/appChatStore'
import { whatsappListingNumbers } from '@/config/whatsappListingNumbers'
import type { LocaleCode } from '@/config/locale-domains'

const props = withDefaults(defineProps<Props>(), {
  faceToFaceVisitEnabled: true
})

const appChatStore = useAppChatStore()
const { $whatsappNumbers } = useNuxtApp()

const { locale, t, tm, rt } = useI18n()
const route = useRoute()
const slug = route.params.slug as string

const whatsappHref = computed(() => {
  const number = whatsappListingNumbers[slug] ?? ($whatsappNumbers as Record<LocaleCode, string>)[locale.value as LocaleCode]
  const text = encodeURIComponent(`Hola me gustaría más información sobre ${props.listing.name}`)
  return `https://wa.me/${number}?text=${text}`
})

const showButtonWithChips = ref(false)

const chips = tm('chips')

const showChip = (id) => {
  if (id === 'brochure') return props.listing.ims.has_brief

  const isCountryAllowed = locale.value === 'mx'
  if (id === 'inperson-schedule-visit') return isCountryAllowed && props?.faceToFaceVisitEnabled !== false

  return true
}

const handleChipClick = (chip) => {
  const initialChatValues = {
    contactFormTitleCopy: rt(chip.form.title),
    contactFormCtaCopy: rt(chip.form.cta),
    userInitialMessage: rt(chip.message)
  }

  const greetingMessages = [
    {
      text: t('samGpt.greetingGeneral', {
        projectName: props.listing.name
      })
    }
  ]

  const endpointConfig = {
    ...appChatStore.endpointConfig,
    shouldCallBotForGreeting: false,
    startFlow: false
  }

  appChatStore.updateContactFormConfig(initialChatValues)
  appChatStore.openChatWindow(endpointConfig, greetingMessages)
}

defineExpose({
  showButtonWithChips
})

</script>

<script lang="ts">
export default {
  name: 'AppChatButton'
}
</script>

<template>
  <section class="app-chat-button-holder">
    <Transition name="scale">
      <div
        v-if="showButtonWithChips && !appChatStore.isChatWindowOpen"
        class="app-chat-button-with-chips">
        <div class="flex flex-col gap-8 items-end">
          <template
            v-for="(chip) in chips"
            :key="`app-chat-chip-selector-${rt(chip.id)}`">
            <RoomieChipSelector
              v-if="showChip(rt(chip.id))"
              :id="`app-chat-chip-selector-${rt(chip.id)}`"
              :data-lh-id="`${screen}-app-chat-expand-button-${rt(chip.id)}`"
              :is-selected="false"
              icon-placement="right"
              @click="handleChipClick(chip)">
              <template #content>
                {{ rt(chip.label) }}
              </template>

              <template #icon>
                <RoomieIcon
                  custom-styles="chips-icon"
                  :icon-name="rt(chip.icon)" />
              </template>
            </RoomieChipSelector>
          </template>
        </div>

        <button
          id="app-chat-close-tooltip-options"
          :data-lh-id="`${screen}-app-chat-close-chip-options`"
          :aria-label="t('closeOptions')"
          type="button"
          class="circular-button bg-brand-800"
          @click="showButtonWithChips = false">
          <CloseIcon class="w-32 h-32 fill-white pointer-events-none" />
        </button>
      </div>
    </Transition>

    <div
      v-if="!showButtonWithChips || appChatStore.isChatWindowOpen"
      class="app-chat-agent-button">
      <a
        id="app-chat-expand-button"
        aria-label="mostrar u ocultar chat"
        class="circular-button relative"
        :href="whatsappHref"
        target="_blank"
        rel="noopener noreferrer">
        <img
          src="https://assets.lahaus.com/static/images/pdp/BigSam.png?auto=compress,enhance,format"
          alt="Sam"
          class="w-full h-full object-cover pointer-events-none">
      </a>
    </div>
  </section>
</template>

<i18n src="./i18n.json" lang="json" />

<style lang="scss">
.app-chat-button-holder {
  .circular-button {
    @apply w-48 h-48 mt-6 rounded-full shadow-[0_0_10px_#0000004f] flex items-center justify-center
  }

  .app-chat-button-with-chips {
    @apply flex flex-col items-end;
  }

  .app-chat-agent-button {
    @apply relative flex flex-col items-end;
  }

  .roomie-chip-selector {
    @apply bg-carbon-800 border-carbon-800 text-white cursor-pointer;

    box-shadow: 0 0 5px 0 rgba(0 0 0 / 86%);
  }

  .chips-icon {
    @apply w-24 h-24 pointer-events-none;
  }
}
</style>
